<script>
import AnalyticsAllPagesReport from '@/components/analytics/AnalyticsAllPagesReport';
import AnalyticsBrowsersReport from '@/components/analytics/AnalyticsBrowsersReport';
import AnalyticsCitiesReport from '@/components/analytics/AnalyticsCitiesReport';
import AnalyticsContinentsReport from '@/components/analytics/AnalyticsContinentsReport';
import AnalyticsCountriesReport from '@/components/analytics/AnalyticsCountriesReport';
import AnalyticsDepthOfVisitReport from '@/components/analytics/AnalyticsDepthOfVisitReport';
import AnalyticsDevicesReport from '@/components/analytics/AnalyticsDevicesReport';
import AnalyticsExitPagesReport from '@/components/analytics/AnalyticsExitPagesReport';
import AnalyticsKeywordsReport from '@/components/analytics/AnalyticsKeywordsReport';
import AnalyticsLandingPagesReport from '@/components/analytics/AnalyticsLandingPagesReport';
import AnalyticsLanguagesReport from '@/components/analytics/AnalyticsLanguagesReport';
import AnalyticsLengthOfVisitReport from '@/components/analytics/AnalyticsLengthOfVisitReport';
import AnalyticsMediumsReport from '@/components/analytics/AnalyticsMediumsReport';
import AnalyticsMobileDevicesReport from '@/components/analytics/AnalyticsMobileDevicesReport';
import AnalyticsOperatingSystemsReport from '@/components/analytics/AnalyticsOperatingSystemsReport';
import AnalyticsPageviewsReport from '@/components/analytics/AnalyticsPageviewsReport';
import AnalyticsReferringUrlsReport from '@/components/analytics/AnalyticsReferringUrlsReport';
import AnalyticsScreenColorsReport from '@/components/analytics/AnalyticsScreenColorsReport';
import AnalyticsScreenResolutionsReport from '@/components/analytics/AnalyticsScreenResolutionsReport';
import AnalyticsTrafficSourcesReport from '@/components/analytics/AnalyticsTrafficSourcesReport';
import AnalyticsVisitorInfoReport from '@/components/analytics/AnalyticsVisitorInfoReport';
import ContentBox from '@/components/ui/ContentBox';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import chartColors from '@/modules/colors/chart-colors';
import debounce from 'lodash/debounce';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'Analytics',
  components: {
    AnalyticsAllPagesReport,
    AnalyticsBrowsersReport,
    AnalyticsCitiesReport,
    AnalyticsContinentsReport,
    AnalyticsCountriesReport,
    AnalyticsDepthOfVisitReport,
    AnalyticsDevicesReport,
    AnalyticsExitPagesReport,
    AnalyticsKeywordsReport,
    AnalyticsLandingPagesReport,
    AnalyticsLanguagesReport,
    AnalyticsLengthOfVisitReport,
    AnalyticsMediumsReport,
    AnalyticsMobileDevicesReport,
    AnalyticsOperatingSystemsReport,
    AnalyticsPageviewsReport,
    AnalyticsReferringUrlsReport,
    AnalyticsScreenColorsReport,
    AnalyticsScreenResolutionsReport,
    AnalyticsTrafficSourcesReport,
    AnalyticsVisitorInfoReport,
    ContentBox,
    DashboardLayout
  },
  metaInfo() {
    return {
      title: this.title
    };
  },
  data() {
    return {
      title: this.$t('Analytics'),
      breadcrumbs: [],
      chartColors,
      dateRange: [this.getDateFromToday(-7), this.getDateFromToday(0)],
      hasError: false,
      pickerOptions: {
        disabledDate: date => {
          return date > new Date() || date < new Date(2005, 0, 0); // earliest date for GA
        },
        shortcuts: [
          [0, 0, 'Today'],
          [-1, -1, 'Yesterday'],
          [-7, 0, 'Last {days} days'],
          [-14, 0, 'Last {days} days'],
          [-30, 0, 'Last {days} days'],
          [-60, 0, 'Last {days} days'],
          [-90, 0, 'Last {days} days'],
          [-180, 0, 'Last {days} days'],
          [-365, 0, 'Last year']
        ].map(val => {
          const days = val[1] - val[0];
          const start = this.getDateFromToday(val[0]);
          const end = this.getDateFromToday(val[1]);
          const text = this.$t(val[2], { days });
          return {
            text,
            onClick: picker => picker.$emit('pick', [start, end])
          };
        })
      },
      reports: {
        pageviews: null,
        visitorInfo: null,
        mediums: null,
        continents: null,
        countries: null,
        cities: null,
        languages: null,
        lengthOfVisit: null,
        depthOfVisit: null,
        allPages: null,
        landingPages: null,
        exitPages: null,
        keywords: null,
        trafficSources: null,
        referringUrls: null,
        browsers: null,
        operatingSystems: null,
        devices: null,
        mobileDevices: null,
        screenColors: null,
        screenResolutions: null
      },
      site: {},
      statusCode: null,
      tabs: {
        // Default tabs
        primary: 'overview',
        visitors: 'continents',
        content: 'allPages',
        traffic: 'keywords',
        technical: 'browsers'
      },
      tabsOnTop: false
    };
  },
  computed: {
    startDate() {
      const date = this.dateRange[0];
      return date instanceof Date ? date.toISOString().split('T')[0] : '';
    },
    endDate() {
      const date = this.dateRange[1];
      return date instanceof Date ? date.toISOString().split('T')[0] : '';
    }
  },
  created() {
    // Fetch site data
    this.$http
      .get(`/sites/${this.$route.params.siteId}`)
      .then(res => {
        this.site = res.data.site;
        this.breadcrumbs = [
          { text: this.$t('Sites'), to: { name: 'Sites' } },
          { text: this.site.name, to: { name: 'Site', params: { siteId: this.site.id } } },
          { text: this.$t('Analytics') }
        ];
        this.statusCode = 200;

        // Bump to the site view if the site is on lockdown
        if (this.site.isLocked) {
          this.$router.push({ name: 'Site', params: { siteId: this.site.id } });
          return;
        } else {
          // Load the initial report
          return this.loadReport(this.tabs.primary);
        }
      })
      .catch(err => (this.statusCode = err.response.status));

    window.addEventListener('resize', this.handleResize);
  },
  mounted() {
    this.handleResize();
  },
  beforeRouteLeave(to, from, next) {
    window.removeEventListener('resize', this.handleResize);
    next();
  },
  methods: {
    // Fetches an analytics report
    fetchReport(params) {
      params = Object.assign(
        {
          dimensions: '',
          metrics: '',
          sort: '',
          startDate: this.startDate,
          endDate: this.endDate,
          maxResults: 1000
        },
        params
      );

      return this.$http.get(`/services/ga/site/${this.site.id}/report`, { params });
    },

    // Returns a date object relative to the current date
    getDateFromToday(numDays) {
      return new Date(new Date().setDate(new Date().getDate() + numDays));
    },

    // Handles changes in the date picker
    handleDateChange() {
      Object.keys(this.reports).forEach(key => (this.reports[key] = null));
      this.loadReport(this.tabs.primary);
    },

    // Handles window resizing
    handleResize: debounce(function() {
      this.tabsOnTop = window.innerWidth < 992;
    }, 100),

    // Loads a report
    loadReport(name) {
      const reportsToLoad = [];

      // Map top-level tabs to the selected subtab name
      if (['visitors', 'content', 'traffic', 'technical'].includes(name)) {
        name = this.tabs[name];
      }

      // Nothing to do if the report is already loaded
      if (name === 'overview') {
        if (this.reports.pageviews && this.reports.visitorInfo && this.reports.mediums) {
          return;
        }
      } else if (this.reports[name]) {
        return;
      }

      switch (name) {
        case 'overview':
          reportsToLoad.push(
            this.fetchReport({
              metrics: 'ga:pageviews,ga:visits',
              dimensions: 'ga:date',
              sort: 'ga:date'
            }).then(res => (this.reports.pageviews = res.data.report)),
            this.fetchReport({
              metrics:
                'ga:bounces,ga:entrances,ga:exits,ga:newVisits,ga:pageviews,ga:timeOnPage,ga:sessionDuration,ga:visitors,ga:visits',
              dimensions: '',
              sort: null
            }).then(res => (this.reports.visitorInfo = res.data.report)),
            this.fetchReport({
              metrics: 'ga:visits',
              dimensions: 'ga:medium',
              sort: '-ga:visits'
            }).then(res => (this.reports.mediums = res.data.report))
          );
          break;
        case 'continents':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:continent',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.continents = res.data.report))
          );
          break;
        case 'countries':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:country',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.countries = res.data.report))
          );
          break;
        case 'cities':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:city',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.cities = res.data.report))
          );
          break;
        case 'languages':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:language',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.languages = res.data.report))
          );
          break;
        case 'lengthOfVisit':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:visitLength',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.lengthOfVisit = res.data.report))
          );
          break;
        case 'depthOfVisit':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:pageDepth',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.depthOfVisit = res.data.report))
          );
          break;
        case 'allPages':
          reportsToLoad.push(
            this.fetchReport({
              metrics: 'ga:visits',
              dimensions: 'ga:pagePath,ga:pageTitle',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.allPages = res.data.report))
          );
          break;
        case 'landingPages':
          reportsToLoad.push(
            this.fetchReport({
              metrics: 'ga:visits',
              dimensions: 'ga:landingPagePath,ga:pageTitle',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.landingPages = res.data.report))
          );
          break;
        case 'exitPages':
          reportsToLoad.push(
            this.fetchReport({
              metrics: 'ga:visits',
              dimensions: 'ga:exitPagePath,ga:pageTitle',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.exitPages = res.data.report))
          );
          break;
        case 'keywords':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:keyword',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.keywords = res.data.report))
          );
          break;
        case 'trafficSources':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:source',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.trafficSources = res.data.report))
          );
          break;
        case 'referringUrls':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:fullReferrer',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.referringUrls = res.data.report))
          );
          break;
        case 'browsers':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:browser',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.browsers = res.data.report))
          );
          break;
        case 'operatingSystems':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:operatingSystem',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.operatingSystems = res.data.report))
          );
          break;
        case 'devices':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:deviceCategory',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.devices = res.data.report))
          );
          break;
        case 'mobileDevices':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:mobileDeviceInfo',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.mobileDevices = res.data.report))
          );
          break;
        case 'screenColors':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:screenColors',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.screenColors = res.data.report))
          );
          break;
        case 'screenResolutions':
          reportsToLoad.push(
            this.fetchReport({
              dimensions: 'ga:screenResolution',
              metrics: 'ga:visits',
              sort: '-ga:visits',
              maxResults: 100
            }).then(res => (this.reports.screenResolutions = res.data.report))
          );
          break;
      }

      // Wait for each report to load
      Promise.all(reportsToLoad).catch(err => {
        this.hasError = true;
        this.$notify({
          type: 'error',
          title: this.$t('Error'),
          message: getErrorMessage(err.response.data.code)
        });
      });
    }
  }
};
</script>

<template>
  <dashboard-layout :status-code="statusCode" size="large">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Analytics options -->
    <div class="analytics-options">
      <el-date-picker
        ref="datePicker"
        v-model="dateRange"
        :clearable="false"
        :start-placeholder="$t('Start Date')"
        :end-placeholder="$t('End Date')"
        :range-separator="$t('to')"
        :picker-options="pickerOptions"
        type="daterange"
        size="small"
        unlink-panels
        class="date-picker"
        @change="handleDateChange"
      />
    </div>

    <!-- Body -->
    <content-box class="analytics-body">
      <!-- Tabs -->
      <el-tabs
        v-model="tabs.primary"
        tab-position="top"
        @tab-click="loadReport($event.name)"
      >
        <!-- Overview -->
        <el-tab-pane :label="$t('Overview')" name="overview">
          <!-- Pageviews -->
          <analytics-pageviews-report
            ref="pageviewsChart"
            :colors="chartColors"
            :data="reports.pageviews"
            :loading="!hasError && reports.pageviews === null"
          />

          <el-row>
            <el-col :xs="24" :sm="24" :md="24" :lg="12" :xl="12">
              <!-- Visitor info -->
              <analytics-visitor-info-report
                ref="visitorInfo"
                :data="reports.visitorInfo"
                :loading="!hasError && reports.visitorInfo === null"
              />
            </el-col>
            <el-col :xs="24" :sm="24" :md="24" :lg="12" :xl="12">
              <!-- Mediums -->
              <analytics-mediums-report
                ref="mediumsChart"
                :colors="chartColors"
                :data="reports.mediums"
                :loading="!hasError && reports.mediums === null"
              />
            </el-col>
          </el-row>
        </el-tab-pane>

        <!-- Visitors -->
        <el-tab-pane :label="$t('Visitors')" name="visitors">
          <el-tabs
            v-model="tabs.visitors"
            :tab-position="tabsOnTop ? 'top' : 'left'"
            @tab-click="loadReport($event.name)"
          >
            <!-- Continents -->
            <el-tab-pane :label="$t('Continents')" name="continents">
              <analytics-continents-report
                :colors="chartColors"
                :data="reports.continents"
                :loading="!hasError && reports.continents === null"
              />
            </el-tab-pane>

            <!-- Countries -->
            <el-tab-pane :label="$t('Countries')" name="countries">
              <analytics-countries-report
                :colors="chartColors"
                :data="reports.countries"
                :loading="!hasError && reports.countries === null"
              />
            </el-tab-pane>

            <!-- Cities -->
            <el-tab-pane :label="$t('Cities')" name="cities">
              <analytics-cities-report
                :colors="chartColors"
                :data="reports.cities"
                :loading="!hasError && reports.cities === null"
              />
            </el-tab-pane>

            <!-- Languages -->
            <el-tab-pane :label="$t('Languages')" name="languages">
              <analytics-languages-report
                :colors="chartColors"
                :data="reports.languages"
                :loading="!hasError && reports.languages === null"
              />
            </el-tab-pane>

            <!-- Length of Visit -->
            <el-tab-pane :label="$t('Length of Visit')" name="lengthOfVisit">
              <analytics-length-of-visit-report
                :colors="chartColors"
                :data="reports.lengthOfVisit"
                :loading="!hasError && reports.lengthOfVisit === null"
              />
            </el-tab-pane>

            <!-- Depth of Visit -->
            <el-tab-pane :label="$t('Depth of Visit')" name="depthOfVisit">
              <analytics-depth-of-visit-report
                :colors="chartColors"
                :data="reports.depthOfVisit"
                :loading="!hasError && reports.depthOfVisit === null"
              />
            </el-tab-pane>
          </el-tabs>
        </el-tab-pane>

        <!-- Content -->
        <el-tab-pane :label="$t('Content')" name="content">
          <el-tabs
            v-model="tabs.content"
            :tab-position="tabsOnTop ? 'top' : 'left'"
            @tab-click="loadReport($event.name)"
          >
            <!-- Top content -->
            <el-tab-pane :label="$t('All Pages')" name="allPages">
              <analytics-all-pages-report
                :data="reports.allPages"
                :loading="!hasError && reports.allPages === null"
              />
            </el-tab-pane>

            <!-- Landing pages -->
            <el-tab-pane :label="$t('Landing Pages')" name="landingPages">
              <analytics-landing-pages-report
                :data="reports.landingPages"
                :loading="!hasError && reports.landingPages === null"
              />
            </el-tab-pane>

            <!-- Exit pages -->
            <el-tab-pane :label="$t('Exit Pages')" name="exitPages">
              <analytics-exit-pages-report
                :data="reports.exitPages"
                :loading="!hasError && reports.exitPages === null"
              />
            </el-tab-pane>
          </el-tabs>
        </el-tab-pane>

        <!-- Traffic -->
        <el-tab-pane :label="$t('Traffic')" name="traffic">
          <el-tabs
            v-model="tabs.traffic"
            :tab-position="tabsOnTop ? 'top' : 'left'"
            @tab-click="loadReport($event.name)"
          >
            <!-- Keywords -->
            <el-tab-pane :label="$t('Keywords')" name="keywords">
              <analytics-keywords-report
                :data="reports.keywords"
                :loading="!hasError && reports.keywords === null"
              />
            </el-tab-pane>

            <!-- Traffic Sources -->
            <el-tab-pane :label="$t('Traffic Sources')" name="trafficSources">
              <analytics-traffic-sources-report
                :data="reports.trafficSources"
                :loading="!hasError && reports.trafficSources === null"
              />
            </el-tab-pane>

            <!-- Referring URLs -->
            <el-tab-pane :label="$t('Referring URLs')" name="referringUrls">
              <analytics-referring-urls-report
                :data="reports.referringUrls"
                :loading="!hasError && reports.referringUrls === null"
              />
            </el-tab-pane>
          </el-tabs>
        </el-tab-pane>

        <!-- Technical -->
        <el-tab-pane :label="$t('Technical')" name="technical">
          <el-tabs
            v-model="tabs.technical"
            :tab-position="tabsOnTop ? 'top' : 'left'"
            @tab-click="loadReport($event.name)"
          >
            <!-- Browsers -->
            <el-tab-pane :label="$t('Browsers')" name="browsers">
              <analytics-browsers-report
                :colors="chartColors"
                :data="reports.browsers"
                :loading="!hasError && reports.browsers === null"
              />
            </el-tab-pane>

            <!-- Operating Systems -->
            <el-tab-pane :label="$t('Operating Systems')" name="operatingSystems">
              <analytics-operating-systems-report
                :colors="chartColors"
                :data="reports.operatingSystems"
                :loading="!hasError && reports.operatingSystems === null"
              />
            </el-tab-pane>

            <!-- Devices -->
            <el-tab-pane :label="$t('Devices')" name="devices">
              <analytics-devices-report
                :colors="chartColors"
                :data="reports.devices"
                :loading="!hasError && reports.devices === null"
              />
            </el-tab-pane>

            <!-- Mobile Devices -->
            <el-tab-pane :label="$t('Mobile Devices')" name="mobileDevices">
              <analytics-mobile-devices-report
                :colors="chartColors"
                :data="reports.mobileDevices"
                :loading="!hasError && reports.mobileDevices === null"
              />
            </el-tab-pane>

            <!-- Screen colors -->
            <el-tab-pane :label="$t('Screen Colors')" name="screenColors">
              <analytics-screen-colors-report
                :colors="chartColors"
                :data="reports.screenColors"
                :loading="!hasError && reports.screenColors === null"
              />
            </el-tab-pane>

            <!-- Screen resolution -->
            <el-tab-pane :label="$t('Screen Resolutions')" name="screenResolutions">
              <analytics-screen-resolutions-report
                :colors="chartColors"
                :data="reports.screenResolutions"
                :loading="!hasError && reports.screenResolutions === null"
              />
            </el-tab-pane>
          </el-tabs>
        </el-tab-pane>
      </el-tabs>
    </content-box>
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.analytics-options {
  text-align: right;
  margin-bottom: 1rem;

  .date-picker {
    width: 14rem;

    /deep/ {
      .el-range-separator {
        width: 1.5rem;
      }

      .el-range__close-icon {
        display: none;
      }
    }
  }
}
</style>
